import { Injectable } from '@angular/core';
import { Filter, CustomRule, AdvancedConditions, DisplayOptions } from '../../models/filter-models';
import { ArmourType, Comparator, DisplayType, GearSizeType, Rarity, WeaponType, YesNoAny } from "../../models/enums";
import { TieredUnique, TieredChanceBase, UniqueTierType, ChanceBaseTierType } from '../../models/item-data-models';
import { RuleGeneratorService } from './rule-generator.service';
import { UniquesTierService } from '../tiering/uniques-tier.service';
import { ChanceBasesTierService } from '../tiering/chance-bases-tier.service';
import { poe1, poe2 } from '../../../game';
import { ItemDataService } from '../item-data.service';
import { EndgameContentPresetService } from '../endgame-content-preset.service';
import { itemTypesWithTwoSockets } from '../../data/poe2/item-lists';

@Injectable({
  providedIn: 'root'
})
export class UniqueGeneratorService {
  private tieredUniques: Record<UniqueTierType, TieredUnique[]> = {
    Excellent: [] as TieredUnique[],
    Good: [] as TieredUnique[],
    Potential: [] as TieredUnique[],
    Restricted: [] as TieredUnique[],
    Class: [] as TieredUnique[],
    Other: [] as TieredUnique[],
  };

  private tieredChanceBases: Record<ChanceBaseTierType, TieredChanceBase[]> = {
    Searching: [] as TieredChanceBase[],
    Potential: [] as TieredChanceBase[],
    Class: [] as TieredChanceBase[],
    Other: [] as TieredChanceBase[],
  };

  constructor(private uniquesTierService: UniquesTierService,
    private chanceBasesTierService: ChanceBasesTierService,
    private ruleGenerator: RuleGeneratorService,
    private itemDataService: ItemDataService,
    private endgameContentPresetService: EndgameContentPresetService,
  ) {
    this.tieredUniques = this.uniquesTierService.getTieredUniques();
    this.tieredChanceBases = this.chanceBasesTierService.getTieredChanceBases();
  }

  public generateChanceBaseHighlightRules(filter: Filter) {
    const searchingBaseTypes = this.tieredChanceBases['Searching'].map(t => t.baseType);
    const potentialBaseTypes = this.tieredChanceBases['Potential'].map(t => t.baseType).concat(this.tieredChanceBases['Class'].map(t => t.baseType));

    const seachingRule = searchingBaseTypes && searchingBaseTypes.length > 0 && filter.chanceBaseTierCosmetics[0].displayOptions.active ? new CustomRule({
      baseTypes: searchingBaseTypes,
      rarityComparator: Comparator.Equal,
      rarity: Rarity.Normal,
      displayOptions: filter.chanceBaseTierCosmetics[0].displayOptions,
      advancedConditions: new AdvancedConditions({ corrupted: YesNoAny.No })
    }) : null;

    const potentialRule = potentialBaseTypes && potentialBaseTypes.length > 0 && filter.chanceBaseTierCosmetics[1].displayOptions.active ? new CustomRule({
      baseTypes: potentialBaseTypes,
      rarityComparator: Comparator.Equal,
      rarity: Rarity.Normal,
      displayOptions: filter.chanceBaseTierCosmetics[1].displayOptions,
      advancedConditions: new AdvancedConditions({ corrupted: YesNoAny.No })
    }) : null;

    return (seachingRule ? this.ruleGenerator.generateRule(seachingRule, filter, 'chance-bases-searching', 'Favourite Chance Bases') : '')
      + '\n\n' + (potentialRule ? this.ruleGenerator.generateRule(potentialRule, filter, 'chance-bases-potential', 'Potential Chance Bases') : '');
  }

  public generateUniqueHighlightRules(filter: Filter) {
    this.tieredUniques = this.uniquesTierService.getTieredUniques();

    const excellentActive = filter.uniqueTierCosmetics[0].displayOptions.active && !filter.uniqueTierCosmetics[0].displayOptions.hidden;
    const goodActive = filter.uniqueTierCosmetics[1].displayOptions.active && !filter.uniqueTierCosmetics[1].displayOptions.hidden;
    const potentialActive = filter.uniqueTierCosmetics[2].displayOptions.active && !filter.uniqueTierCosmetics[2].displayOptions.hidden;
    const restrictedActive = filter.uniqueTierCosmetics[3].displayOptions.active && !filter.uniqueTierCosmetics[3].displayOptions.hidden;
    const otherActive = filter.uniqueTierCosmetics[4].displayOptions.active && !filter.uniqueTierCosmetics[4].displayOptions.hidden;

    const excellentBaseTypes = [...new Set(this.tieredUniques['Excellent'].map(t => t.baseType))];
    const goodBaseTypes = [...new Set(this.tieredUniques['Good'].map(t => t.baseType))].filter(g => !excellentBaseTypes.includes(g));
    const potentialBaseTypes = [...new Set(this.tieredUniques['Potential'].map(t => t.baseType).concat(this.tieredUniques['Class'].map(t => t.baseType)))].filter(p => !excellentBaseTypes.includes(p) && !goodBaseTypes.includes(p));
    const restrictedBaseTypes = [...new Set(this.tieredUniques['Restricted'].map(t => t.baseType))].filter(r => !excellentBaseTypes.includes(r) && !goodBaseTypes.includes(r) && !potentialBaseTypes.includes(r));
    const favouriteRestrictedBaseTypes = this.endgameContentPresetService.getFavouriteRestrictedBossBaseTypes(filter, this.tieredUniques['Restricted']);
    const favouriteRestrictedSet = new Set(favouriteRestrictedBaseTypes);
    const otherRestrictedBaseTypes = restrictedBaseTypes.filter(b => !favouriteRestrictedSet.has(b));
    const bossOnlyLowValueBossUniques = this.endgameContentPresetService.getBossOnlyLowValueBossUniques(filter, this.tieredUniques['Other']);
    const favouriteLowValueBaseTypes = filter.showFavouriteLowValueBossUniques
      ? bossOnlyLowValueBossUniques.filter(x => x.isFavourite).map(x => x.baseType)
      : [];
    const otherLowValueBossBaseTypes = filter.showOtherLowValueBossUniques
      ? bossOnlyLowValueBossUniques.filter(x => !x.isFavourite).map(x => x.baseType)
      : [];
    const favouriteLowValueSet = new Set(favouriteLowValueBaseTypes);
    const otherLowValueBossSet = new Set(otherLowValueBossBaseTypes);
    const otherBaseTypes = [...new Set(this.tieredUniques['Other'].map(t => t.baseType))]
      .filter(r => !excellentBaseTypes.includes(r) && !goodBaseTypes.includes(r) && !potentialBaseTypes.includes(r) && !restrictedBaseTypes.includes(r));
    const genericOtherBaseTypes = otherBaseTypes.filter(b => !favouriteLowValueSet.has(b) && !otherLowValueBossSet.has(b));

    const excellentUniquesRule = new CustomRule({
      baseTypes: excellentBaseTypes,
      rarityComparator: Comparator.Equal,
      rarity: Rarity.Unique,
      displayOptions: filter.uniqueTierCosmetics[0].displayOptions
    });

    const goodUniquesRule = new CustomRule({
      baseTypes: goodBaseTypes,
      rarityComparator: Comparator.Equal,
      rarity: Rarity.Unique,
      displayOptions: filter.uniqueTierCosmetics[1].displayOptions
    });

    const potentialUniquesRule = new CustomRule({
      baseTypes: potentialBaseTypes,
      rarityComparator: Comparator.Equal,
      rarity: Rarity.Unique,
      displayOptions: filter.uniqueTierCosmetics[2].displayOptions
    });

    const restrictedUniquesRule = new CustomRule({
      baseTypes: restrictedBaseTypes,
      rarityComparator: Comparator.Equal,
      rarity: Rarity.Unique,
      displayOptions: filter.uniqueTierCosmetics[3].displayOptions
    });

    const otherUniquesRule = new CustomRule({
      baseTypes: genericOtherBaseTypes,
      rarityComparator: Comparator.Equal,
      rarity: Rarity.Unique,
      displayOptions: filter.uniqueTierCosmetics[4].displayOptions
    });

    let foulbornUniquesRule = '';
    if (poe1 && filter.showFoulbornUniques) {
      const options = filter.customRuleStyles.find(s => s.ruleId === 'uniques-foulborn')!.options!;
      foulbornUniquesRule = this.ruleGenerator.generateRule(
        new CustomRule({
          rarityComparator: Comparator.Equal,
          rarity: Rarity.Unique,
          displayOptions: new DisplayOptions({ displayType: DisplayType.Custom, cosmeticOptions: options }),
          advancedConditions: new AdvancedConditions({ foulbornUnique: true })
        }), filter, 'uniques-foulborn', 'Foulborn Uniques'
      );
    }

    let uniqueMapsRule = '';
    if (poe1 && filter.showUniqueMaps) {
      const options = filter.customRuleStyles.find(s => s.ruleId === 'uniques-maps')!.options!;
      uniqueMapsRule = this.ruleGenerator.generateRule(
        new CustomRule({
          itemClass: 'Maps',
          rarityComparator: Comparator.Equal,
          rarity: Rarity.Unique,
          displayOptions: new DisplayOptions({ displayType: DisplayType.Custom, cosmeticOptions: options })
        }), filter, 'uniques-maps', 'Unique Maps'
      );
    } else if (poe1 && !filter.showUniqueMaps) {
      uniqueMapsRule = this.ruleGenerator.generateRule(
        new CustomRule({
          itemClass: 'Maps',
          rarityComparator: Comparator.Equal,
          rarity: Rarity.Unique,
          displayOptions: new DisplayOptions({ hidden: true })
        }), filter, 'uniques-maps', 'Unique Maps'
      );
    }

    let vaalUniquesRule = '';
    if (poe2 && filter.showVaalUniques) {
      const options = filter.customRuleStyles.find(s => s.ruleId === 'uniques-vaal')!.options!;
      vaalUniquesRule = this.ruleGenerator.generateRule(
        new CustomRule({
          rarityComparator: Comparator.Equal,
          rarity: Rarity.Unique,
          displayOptions: new DisplayOptions({ displayType: DisplayType.Custom, cosmeticOptions: options }),
          advancedConditions: new AdvancedConditions({ isVaalUnique: true })
        }), filter, 'uniques-vaal', 'Vaal Uniques'
      );
    }

    let vaalModUniquesRule = '';
    if (poe2 && filter.showVaalModUniques) {
      const options = filter.customRuleStyles.find(s => s.ruleId === 'uniques-vaal-mod')!.options!;
      vaalModUniquesRule = this.ruleGenerator.generateRule(
        new CustomRule({
          rarityComparator: Comparator.Equal,
          rarity: Rarity.Unique,
          displayOptions: new DisplayOptions({ displayType: DisplayType.Custom, cosmeticOptions: options }),
          advancedConditions: new AdvancedConditions({ hasVaalUniqueMod: true })
        }), filter, 'uniques-vaal-mod', 'Vaal Mod Uniques'
      );
    }

    let exceptionalUniquesRule = '';

    if (poe2 && filter.showExceptionalUniques) {
      const options = filter.customRuleStyles.find(s => s.ruleId === 'uniques-exceptional')!.options!;
      const weaponTypesWithTwoSockets = this.itemDataService.getAllWeaponTypes().filter(wt => itemTypesWithTwoSockets.includes(wt) && wt != WeaponType.Quivers) as WeaponType[];
      const weaponTypesWithOneSocket = this.itemDataService.getAllWeaponTypes().filter(wt => !itemTypesWithTwoSockets.includes(wt) && wt != WeaponType.Quivers) as WeaponType[];
      const armourTypesWithTwoSockets = this.itemDataService.getAllArmourTypes().filter(at => itemTypesWithTwoSockets.includes(at)) as ArmourType[];
      const armourTypesWithOneSocket = this.itemDataService.getAllArmourTypes().filter(at => !itemTypesWithTwoSockets.includes(at)) as ArmourType[];

      exceptionalUniquesRule = this.ruleGenerator.generateRule(
        new CustomRule({
          filterItemClass: weaponTypesWithTwoSockets.map(i => `"${i}"`).join(' ') + ' ' + armourTypesWithTwoSockets.map(i => `"${i}"`).join(' '),
          advancedConditions: new AdvancedConditions({ minSockets: 3 }),
          rarityComparator: Comparator.Equal,
          rarity: Rarity.Unique,
          displayOptions: new DisplayOptions({ displayType: DisplayType.Custom, cosmeticOptions: options })
        }), filter, 'uniques-exceptional', 'Exceptional 2-Socketed Uniques'
      ) + '\n\n' + this.ruleGenerator.generateRule(
        new CustomRule({
          filterItemClass: weaponTypesWithOneSocket.map(i => `"${i}"`).join(' ') + ' ' + armourTypesWithOneSocket.map(i => `"${i}"`).join(' '),
          advancedConditions: new AdvancedConditions({ minSockets: 2 }),
          rarityComparator: Comparator.Equal,
          rarity: Rarity.Unique,
          displayOptions: new DisplayOptions({ displayType: DisplayType.Custom, cosmeticOptions: options })
        }), filter, 'uniques-exceptional', 'Exceptional 1-Socketed Uniques'
      ) + '\n\n' + this.ruleGenerator.generateRule(
        new CustomRule({
          advancedConditions: new AdvancedConditions({ minQuality: filter.showExceptionalUniquesMinQuality ?? 21 }),
          rarityComparator: Comparator.Equal,
          rarity: Rarity.Unique,
          displayOptions: new DisplayOptions({ displayType: DisplayType.Custom, cosmeticOptions: options })
        }), filter, 'uniques-exceptional', 'Exceptional Quality Uniques'
      );
    }

    let smallUniquesRule = '';
    if (poe2 && filter.showSmallUniques) {
      const options = filter.customRuleStyles.find(s => s.ruleId === 'uniques-small')!.options!;

      let size = '';

      if (filter.showSmallUniquesMaxSize === GearSizeType.TwoByTwoOnly) {
        size = `
  Width <= 2
  Height <= 2`;
      } else if (filter.showSmallUniquesMaxSize === GearSizeType.SmallOnly) {
        size = `
  Width <= 2
  Height <= 2`;
      } else if (filter.showSmallUniquesMaxSize === GearSizeType.JewelleryOnly) {
        size = `
  Height == 1`;
      }

      smallUniquesRule = this.ruleGenerator.generateRule(
        new CustomRule({
          rarityComparator: Comparator.Equal,
          rarity: Rarity.Unique,
          displayOptions: new DisplayOptions({ displayType: DisplayType.Custom, cosmeticOptions: options })
        }), filter, 'uniques-small', 'Small Disenchant Uniques', { customLine: size }
      );

      if (filter.showSmallUniquesMaxSize === GearSizeType.SmallOnly) {
        smallUniquesRule += '\n\n' + this.ruleGenerator.generateRule(
          new CustomRule({
            rarityComparator: Comparator.Equal,
            rarity: Rarity.Unique,
            displayOptions: new DisplayOptions({ displayType: DisplayType.Custom, cosmeticOptions: options })
          }), filter, 'uniques-small', 'Small Disenchant Uniques', { customLine: `
  Width == 1` }
        );
      }
    }

    const otherRemainingBaseTypes = otherActive
      ? []
      : filter.hideAllRemainingUniques && (filter.showFavouriteLowValueBossUniques || filter.showOtherLowValueBossUniques)
        ? otherBaseTypes.filter(b =>
          !(filter.showFavouriteLowValueBossUniques && favouriteLowValueSet.has(b)) &&
          !(filter.showOtherLowValueBossUniques && otherLowValueBossSet.has(b))
        )
        : otherBaseTypes;

    const remainingBaseTypes = [
      ...(excellentActive ? [] : excellentBaseTypes),
      ...(goodActive ? [] : goodBaseTypes),
      ...(potentialActive ? [] : potentialBaseTypes),
      ...(restrictedActive ? [] : otherRestrictedBaseTypes),
      ...otherRemainingBaseTypes,
    ];

    const remainingUniquesRule = new CustomRule({
      baseTypes: remainingBaseTypes,
      rarityComparator: Comparator.Equal,
      rarity: Rarity.Unique,
      displayOptions: filter.hideAllRemainingUniques
        ? new DisplayOptions({ hidden: true })
        : new DisplayOptions() // Just show all remaining
    });

    // Catch-all remaining, unknown base types
    const unknownUniquesRule = new CustomRule({
      rarityComparator: Comparator.Equal,
      rarity: Rarity.Unique,
      displayOptions: filter.uniqueTierCosmetics[0].displayOptions.active ? filter.uniqueTierCosmetics[0].displayOptions : new DisplayOptions()
    });

    const excellentRule = (excellentActive && excellentBaseTypes.length > 0) ? this.ruleGenerator.generateRule(excellentUniquesRule, filter, 'uniques-excellent', 'Excellent Uniques') : '';
    const goodRule = (goodActive && goodBaseTypes.length > 0) ? this.ruleGenerator.generateRule(goodUniquesRule, filter, 'uniques-good', 'Good Uniques') : '';
    const potentialRule = (potentialActive && potentialBaseTypes.length > 0) ? this.ruleGenerator.generateRule(potentialUniquesRule, filter, 'uniques-potential', 'Potential Uniques') : '';
    const restrictedRule = (restrictedActive && restrictedBaseTypes.length > 0) ? this.ruleGenerator.generateRule(restrictedUniquesRule, filter, 'uniques-restricted', 'Restricted Uniques') : '';
    const favouriteRestrictedRule = (!restrictedActive
      && favouriteRestrictedBaseTypes.length > 0) ? this.ruleGenerator.generateRule(
      new CustomRule({
        baseTypes: favouriteRestrictedBaseTypes,
        rarityComparator: Comparator.Equal,
        rarity: Rarity.Unique,
        displayOptions: filter.uniqueTierCosmetics[3].displayOptions,
      }),
      filter,
      'uniques-restricted-favourite-bosses',
      'Favourite Restricted Uniques',
      undefined,
      true,
    ) : '';
    const otherRule = (otherActive && genericOtherBaseTypes.length > 0) ? this.ruleGenerator.generateRule(otherUniquesRule, filter, 'uniques-other', 'Other Uniques') : '';
    const favouriteLowValueRule = (!otherActive
      && filter.hideAllRemainingUniques
      && filter.showFavouriteLowValueBossUniques
      && favouriteLowValueBaseTypes.length > 0) ? this.ruleGenerator.generateRule(
      new CustomRule({
        baseTypes: favouriteLowValueBaseTypes,
        rarityComparator: Comparator.Equal,
        rarity: Rarity.Unique,
        displayOptions: new DisplayOptions(),
      }),
      filter,
      'uniques-favourite-bosses',
      'Favourite Low-value Boss-only Uniques',
      undefined,
      true,
    ) : '';
    const otherLowValueBossRule = (!otherActive
      && filter.hideAllRemainingUniques
      && filter.showOtherLowValueBossUniques
      && otherLowValueBossBaseTypes.length > 0) ? this.ruleGenerator.generateRule(
      new CustomRule({
        baseTypes: otherLowValueBossBaseTypes,
        rarityComparator: Comparator.Equal,
        rarity: Rarity.Unique,
        displayOptions: new DisplayOptions(),
      }),
      filter,
      'uniques-other-bosses',
      'Other Low-value Boss-only Uniques',
      undefined,
      true,
    ) : '';
    const remainingRule = this.ruleGenerator.generateRule(remainingUniquesRule, filter, 'uniques-hide-remaining', 'All Remaining Uniques');

    return excellentRule
      + '\n\n' + goodRule
      + (poe1 && filter.showFoulbornUniques ? '\n\n' + foulbornUniquesRule : '')
      + (poe1 ? '\n\n' + uniqueMapsRule : '')
      + (poe2 && filter.showVaalModUniques ? '\n\n' + vaalModUniquesRule : '')
      + (poe2 && filter.showVaalUniques ? '\n\n' + vaalUniquesRule : '')
      + '\n\n' + potentialRule
      + '\n\n' + restrictedRule
      + (favouriteRestrictedRule ? '\n\n' + favouriteRestrictedRule : '')
      + (poe2 && filter.showExceptionalUniques ? '\n\n' + exceptionalUniquesRule : '')
      + (poe2 && filter.showSmallUniques ? '\n\n' + smallUniquesRule : '')
      + '\n\n' + otherRule
      + (favouriteLowValueRule ? '\n\n' + favouriteLowValueRule : '')
      + (otherLowValueBossRule ? '\n\n' + otherLowValueBossRule : '')
      + '\n\n' + (remainingBaseTypes.length > 0 ? remainingRule : '')
      + '\n\n' + this.ruleGenerator.generateRule(unknownUniquesRule, filter, 'uniques-unknown', 'Unknown Uniques');
  }
}
